/**
 * CSV import: mapping and per-row validation (slice-01 §5, R-1d).
 *
 * Pure. Tokenising the file is somebody else's job — the API hands this an
 * array of already-parsed rows — and everything from there is here, because
 * this is the part with the business rules in it and the part a wrong answer
 * corrupts a roster with.
 *
 * ## Why the errors carry a column *name*
 *
 * §5: "Row 42, date of birth: 13/45/2020 is not a date." Three facts, and each
 * one is doing work:
 *
 *   - **The row number** is the row in *their* file, counting the header, so it
 *     matches what a spreadsheet shows. Off by one here and every error points
 *     at the wrong line, which is worse than no line at all.
 *   - **The column** is *their* header, not our field name and never an index.
 *     An agency reading "column 7 is invalid" has to count columns; an agency
 *     reading "Date of Birth" already knows where to look.
 *   - **The value** is quoted back. "is not a date" alone leaves them hunting
 *     for which of 200 dates was wrong.
 *
 * ## Partial import
 *
 * `validateRows` returns valid and invalid rows separately and never throws.
 * §5 allows the valid ones to land while the invalid ones come back as a CSV to
 * fix — so a validator that stopped at the first bad row would turn a 200-row
 * import with three typos into six round trips.
 */
/** Fields an import can populate. The mapping's left-hand side. */
export const IMPORT_FIELDS = [
    'mrn',
    'firstName',
    'middleName',
    'lastName',
    'preferredName',
    'dateOfBirth',
    'gender',
    'ssnLast4',
    'medicaidId',
    'medicareId',
    'primaryLanguage',
];
/**
 * The fields a row cannot be imported without.
 *
 * `mrn` is here because it is the idempotency key: a row without one cannot be
 * matched against an existing client, so importing it twice would create two.
 * That makes a missing MRN a row error rather than a generated value.
 */
export const REQUIRED_IMPORT_FIELDS = [
    'mrn',
    'firstName',
    'lastName',
    'dateOfBirth',
];
/**
 * Guess a mapping from the file's own headers.
 *
 * A starting point for the mapping UI, never the mapping itself: §5 says column
 * mapping is user-driven, and a guess that silently became the answer is how a
 * "Middle Name" column ends up in `preferredName` on 200 clients.
 *
 * Matching ignores case, spaces, underscores and punctuation, because every
 * export writes the same header a different way — `DOB`, `dob`, `Date of
 * Birth`, `date_of_birth`.
 */
const SYNONYMS = {
    // `normaliseHeader` strips punctuation, so "Client #" arrives as "client"
    // and "Client No." as "clientno". Both are ordinary column names in a real
    // export and neither was matched by the first version of this list.
    mrn: [
        'mrn',
        'client',
        'clientid',
        'clientnumber',
        'clientno',
        'clientnum',
        'recordnumber',
        'chartnumber',
        'id',
    ],
    firstName: ['firstname', 'first', 'givenname', 'fname'],
    middleName: ['middlename', 'middle', 'middleinitial', 'mi'],
    lastName: ['lastname', 'last', 'surname', 'familyname', 'lname'],
    preferredName: ['preferredname', 'preferred', 'goesby', 'nickname'],
    dateOfBirth: ['dateofbirth', 'dob', 'birthdate', 'birthday'],
    gender: ['gender', 'sex'],
    ssnLast4: ['ssnlast4', 'ssn4', 'last4ssn', 'ssnlastfour'],
    medicaidId: ['medicaidid', 'medicaid', 'medicaidnumber', 'manumber'],
    medicareId: ['medicareid', 'medicare', 'medicarenumber', 'mbi'],
    primaryLanguage: ['primarylanguage', 'language', 'preferredlanguage'],
};
const normaliseHeader = (header) => header.toLowerCase().replace(/[^a-z0-9]/g, '');
export function suggestMapping(headers) {
    const suggestion = {};
    const taken = new Set();
    for (const field of IMPORT_FIELDS) {
        const synonyms = SYNONYMS[field];
        const match = headers.find((header) => !taken.has(header) && synonyms.includes(normaliseHeader(header)));
        if (match !== undefined) {
            suggestion[field] = match;
            // One header cannot feed two fields. Without this a file with a single
            // "Name" column would map it to both first and last name and every
            // client would be "Smith Smith".
            taken.add(match);
        }
    }
    return suggestion;
}
/**
 * Is this mapping usable at all?
 *
 * Checked before any row is read, because a mapping missing `dateOfBirth`
 * produces the same error on all 200 rows and that is a fact about the mapping,
 * not about the data. Reporting it 200 times buries it.
 */
export function mappingProblems(mapping, headers) {
    const problems = [];
    const present = new Set(headers);
    for (const field of REQUIRED_IMPORT_FIELDS) {
        const column = mapping[field];
        if (column === undefined || column === '') {
            problems.push(`${LABELS[field]} is not mapped to a column, and every client needs one.`);
            continue;
        }
        if (!present.has(column)) {
            problems.push(`${LABELS[field]} is mapped to "${column}", which is not a column in this file.`);
        }
    }
    const used = new Map();
    for (const field of IMPORT_FIELDS) {
        const column = mapping[field];
        if (column === undefined || column === '')
            continue;
        used.set(column, [...(used.get(column) ?? []), field]);
    }
    for (const [column, fields] of used) {
        if (fields.length > 1) {
            problems.push(`"${column}" is mapped to ${fields.map((field) => LABELS[field]).join(' and ')}. ` +
                'One column can only fill one field.');
        }
    }
    return problems;
}
/** Field names as a person would say them, for every message this file emits. */
const LABELS = {
    mrn: 'client number',
    firstName: 'first name',
    middleName: 'middle name',
    lastName: 'last name',
    preferredName: 'preferred name',
    dateOfBirth: 'date of birth',
    gender: 'gender',
    ssnLast4: 'SSN last 4',
    medicaidId: 'Medicaid ID',
    medicareId: 'Medicare ID',
    primaryLanguage: 'primary language',
};
/**
 * A date, in any of the shapes an agency export actually uses.
 *
 * ISO first, then the two ambiguous slash forms. **`13/45/2020` is rejected
 * rather than guessed**, which is §5's own example, and so is `03/04/2020`
 * being read as April 3rd — the parser accepts `MM/DD/YYYY` only, because this
 * is a US product and silently accepting both orders means half a roster has
 * birthdays transposed and nobody finds out until a claim is denied.
 */
const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
const US_SLASH = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;
export function parseImportDate(raw) {
    const trimmed = raw.trim();
    const iso = ISO.exec(trimmed);
    if (iso)
        return isRealDate(+iso[1], +iso[2], +iso[3]) ? trimmed : null;
    const us = US_SLASH.exec(trimmed);
    if (us) {
        const [month, day, year] = [+us[1], +us[2], +us[3]];
        if (!isRealDate(year, month, day))
            return null;
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
    return null;
}
/**
 * A real calendar date, leap years included.
 *
 * Built from the day counts rather than from `new Date`, which happily rolls
 * February 30th forward to March 2nd — and a roster where three birthdays
 * silently moved is exactly the failure this import exists to avoid.
 */
function isRealDate(year, month, day) {
    if (month < 1 || month > 12 || day < 1)
        return false;
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    const lengths = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return day <= lengths[month - 1];
}
const GENDERS = new Map([
    ['m', 'MALE'],
    ['male', 'MALE'],
    ['f', 'FEMALE'],
    ['female', 'FEMALE'],
    ['x', 'NON_BINARY'],
    ['nonbinary', 'NON_BINARY'],
    ['non-binary', 'NON_BINARY'],
    ['other', 'OTHER'],
    ['u', 'UNKNOWN'],
    ['unknown', 'UNKNOWN'],
    ['declined', 'DECLINED'],
]);
/**
 * Validate every row, and never stop at the first bad one.
 *
 * `rows` are the file's data rows in order, each a header-keyed record.
 * `firstDataRow` is what to call the first of them in a message — 2 for a file
 * with one header row, which is what a spreadsheet shows.
 */
export function validateRows(rows, mapping, firstDataRow = 2) {
    const valid = [];
    const errors = [];
    const seenMrn = new Map();
    rows.forEach((row, index) => {
        const rowNumber = firstDataRow + index;
        const rowErrors = [];
        const read = (field) => {
            const column = mapping[field];
            if (column === undefined)
                return '';
            return (row[column] ?? '').trim();
        };
        const fail = (field, value, message) => {
            rowErrors.push({ row: rowNumber, column: mapping[field] ?? LABELS[field], value, message });
        };
        for (const field of REQUIRED_IMPORT_FIELDS) {
            if (read(field) === '')
                fail(field, '', `${LABELS[field]} is required and this row is blank.`);
        }
        const dateOfBirthRaw = read('dateOfBirth');
        const dateOfBirth = dateOfBirthRaw === '' ? null : parseImportDate(dateOfBirthRaw);
        if (dateOfBirthRaw !== '' && dateOfBirth === null) {
            fail('dateOfBirth', dateOfBirthRaw, `${dateOfBirthRaw} is not a date. Use YYYY-MM-DD or MM/DD/YYYY.`);
        }
        const ssnLast4 = read('ssnLast4');
        if (ssnLast4 !== '' && !/^\d{4}$/.test(ssnLast4)) {
            fail('ssnLast4', ssnLast4, `${ssnLast4} is not the last four digits of an SSN.`);
        }
        const genderRaw = read('gender');
        const gender = genderRaw === '' ? undefined : GENDERS.get(genderRaw.toLowerCase());
        if (genderRaw !== '' && gender === undefined) {
            fail('gender', genderRaw, `${genderRaw} is not a gender this system recognises.`);
        }
        /*
         * A duplicate MRN *within the file*.
         *
         * Import is idempotent on (agency_id, mrn), so two rows carrying the same
         * MRN are not two clients — they are one client written twice, and the
         * second silently wins. Rejecting the later row makes the agency say which
         * one they meant instead of letting row order decide.
         */
        const mrn = read('mrn');
        const firstSeenAt = mrn === '' ? undefined : seenMrn.get(mrn.toLowerCase());
        if (firstSeenAt !== undefined) {
            fail('mrn', mrn, `${mrn} is already used on row ${firstSeenAt} of this file.`);
        }
        else if (mrn !== '') {
            seenMrn.set(mrn.toLowerCase(), rowNumber);
        }
        if (rowErrors.length > 0) {
            errors.push(...rowErrors);
            return;
        }
        const optional = (field) => {
            const value = read(field);
            return value === '' ? undefined : value;
        };
        valid.push({
            row: rowNumber,
            values: {
                mrn,
                firstName: read('firstName'),
                middleName: optional('middleName'),
                lastName: read('lastName'),
                preferredName: optional('preferredName'),
                dateOfBirth: dateOfBirth,
                gender,
                ssnLast4: optional('ssnLast4'),
                medicaidId: optional('medicaidId'),
                medicareId: optional('medicareId'),
                primaryLanguage: optional('primaryLanguage'),
            },
        });
    });
    return { valid, errors };
}
/**
 * The errors as one sentence each, in §26.2's register.
 *
 * "Row 42, date of birth: 13/45/2020 is not a date." Kept here rather than in
 * the API so the wording is asserted by a pure test, and so the downloadable
 * CSV and the on-screen list cannot drift into saying it differently.
 */
export function describeRowError(error) {
    return `Row ${error.row}, ${error.column}: ${error.message}`;
}
//# sourceMappingURL=import.js.map